import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { useSnackbar } from 'react-simple-snackbar'
import {
  fetchCorporateCampaign,
  sendCorporateCampaignEmails,
  sendSingleCorporateEmail,
  downloadCorporatePdf,
} from '../../api/index'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import { useCorporateGuard } from '../../hooks/useRoleGuard'
import Spinner from '../Spinner/Spinner'
import styles from '../ImportMensuel/CampagneDetail.module.css'

const MONTHS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
]

const statusLabel = {
  sent: { text: 'Envoyée', cls: 'sent' },
  failed: { text: 'Échec', cls: 'failed' },
  pending: { text: 'En attente', cls: 'pending' },
}

const IconMail = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const CorporateCampagneDetail = () => {
  const { id } = useParams()
  const history = useHistory()
  const [openSnackbar] = useSnackbar()
  const openSnackbarRef = useRef(openSnackbar)
  openSnackbarRef.current = openSnackbar
  useCorporateGuard()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState([])
  const [sending, setSending] = useState(false)
  const [search, setSearch] = useState('')
  const [confirmModal, setConfirmModal] = useState(null)

  const fetchCampaign = useCallback(async () => {
    try {
      const { data: result } = await fetchCorporateCampaign(id)
      setData(result)
    } catch (error) {
      openSnackbarRef.current(error?.response?.data?.message || 'Erreur')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchCampaign()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => {
    if (!data || sending) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('sendAll') === '1') {
      window.history.replaceState({}, '', `/corporate/campagnes/${id}`)
      requestSendEmails(null)
    }
    // eslint-disable-next-line
  }, [data])

  const { campaign, documents = [] } = data || {}

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return documents
    return documents.filter((d) =>
      [d.clientName, d.fileName, d.client?.email, d.client?.matriculeFiscal]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    )
  }, [documents, search])

  const stats = useMemo(() => {
    const sent = documents.filter((d) => d.emailStatus === 'sent').length
    const failed = documents.filter((d) => d.emailStatus === 'failed').length
    const pending = documents.filter((d) => !d.emailStatus || d.emailStatus === 'pending').length
    const total = sent + pending + failed
    return { sent, failed, pending, progress: total ? Math.round((sent / total) * 100) : 0 }
  }, [documents])

  const readyToSend = useMemo(
    () => documents.filter((d) => d.client?.email && d.emailStatus !== 'sent').length,
    [documents]
  )

  const selectedSendable = useMemo(
    () => selected.filter((docId) => {
      const doc = documents.find((d) => d._id === docId)
      return doc?.client?.email
    }).length,
    [selected, documents]
  )

  const downloadPdf = async (documentId, fileName) => {
    try {
      const response = await downloadCorporatePdf(id, documentId)
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
    } catch {
      openSnackbar('Erreur téléchargement PDF')
    }
  }

  const executeSendEmails = async (documentIds = null) => {
    setSending(true)
    try {
      const { data: result } = await sendCorporateCampaignEmails(id, documentIds ? { documentIds } : {})
      openSnackbar(result.message)
      fetchCampaign()
      setSelected([])
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur envoi')
    } finally {
      setSending(false)
    }
  }

  const requestSendEmails = (documentIds = null) => {
    const count = documentIds ? documentIds.length : documents.length
    if (!count) {
      openSnackbar('Aucun document à envoyer')
      return
    }
    setConfirmModal({
      documentIds,
      variant: 'info',
      title: 'Envoyer les emails',
      message: documentIds
        ? `Envoyer ${count} email(s) sélectionné(s) ?`
        : `Envoyer tous les emails (${count}) ?`,
      detail: 'Les PDF seront joints aux emails des clients convention.',
      confirmLabel: 'Envoyer',
    })
  }

  const handleConfirmModal = () => {
    if (!confirmModal) return
    const { documentIds } = confirmModal
    setConfirmModal(null)
    executeSendEmails(documentIds ?? null)
  }

  const sendOne = async (documentId) => {
    setSending(true)
    try {
      const { data: result } = await sendSingleCorporateEmail(id, documentId)
      openSnackbar(result.message)
      fetchCampaign()
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Erreur envoi')
    } finally {
      setSending(false)
    }
  }

  if (loading) {
    return <div className={styles.loading}><Spinner /></div>
  }

  if (!campaign) return null

  const periodLabel = `${MONTHS[campaign.month]} ${campaign.year}`

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => history.push('/corporate/dashboard')}>
        ← Dashboard Corporate
      </button>

      <div className={styles.topBar}>
        <div>
          <h1>{campaign.label || `Campagne ${periodLabel}`}</h1>
          <p>{campaign.successCount} PDF · Corporate conventions</p>
        </div>
      </div>

      <div className={styles.periodBanner}>
        <strong>Période : {periodLabel}</strong>
        <span>{stats.progress}% emails envoyés</span>
      </div>

      <div className={styles.kpiGrid}>
        <div className={`${styles.kpiCard} ${styles.kpiPrimary}`}>
          <span className={styles.kpiLabel}>PDF</span>
          <span className={styles.kpiValue}>{documents.length}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Envoyés</span>
          <span className={styles.kpiValue}>{stats.sent}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>En attente</span>
          <span className={styles.kpiValue}>{stats.pending}</span>
        </div>
        <div className={styles.kpiCard}>
          <span className={styles.kpiLabel}>Échecs</span>
          <span className={styles.kpiValue}>{stats.failed}</span>
        </div>
      </div>

      <div className={styles.sendPanel}>
        <div className={styles.sendPanelInfo}>
          <h3>Envoi des emails clients</h3>
          <p>
            {stats.pending} en attente · {stats.sent} envoyés
            {stats.failed > 0 ? ` · ${stats.failed} échec(s)` : ''}
          </p>
        </div>
        <div className={styles.sendActions}>
          <button
            type="button"
            className={styles.btnSendSelect}
            disabled={sending || selectedSendable === 0}
            onClick={() => requestSendEmails(selected)}
          >
            <IconMail />
            Sélection
            {selectedSendable > 0 && <span className={styles.sendCount}>{selectedSendable}</span>}
          </button>
          <button
            type="button"
            className={styles.btnSendAll}
            disabled={sending || readyToSend === 0}
            onClick={() => requestSendEmails(null)}
          >
            <IconMail />
            {sending ? 'Envoi en cours…' : 'Envoyer tout'}
            {!sending && readyToSend > 0 && <span className={styles.sendBadge}>{readyToSend}</span>}
          </button>
        </div>
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Documents PDF</h2>
          <input type="search" className={styles.searchInput} placeholder="Rechercher client…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((d) => selected.includes(d._id))}
                    onChange={() => {
                      const ids = filtered.map((d) => d._id)
                      const all = ids.every((i) => selected.includes(i))
                      setSelected(all ? selected.filter((x) => !ids.includes(x)) : [...new Set([...selected, ...ids])])
                    }}
                  />
                </th>
                <th>Client</th>
                <th>Fichier</th>
                <th>Email</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((doc) => {
                const st = statusLabel[doc.emailStatus || 'pending']
                return (
                  <tr key={doc._id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.includes(doc._id)}
                        onChange={() => setSelected((prev) => prev.includes(doc._id) ? prev.filter((x) => x !== doc._id) : [...prev, doc._id])}
                      />
                    </td>
                    <td>{doc.clientName}</td>
                    <td>{doc.fileName}</td>
                    <td>{doc.client?.email || '—'}</td>
                    <td><span className={`${styles.badge} ${styles[st.cls]}`}>{st.text}</span></td>
                    <td className={styles.actionCell}>
                      <button type="button" className={styles.pdfBtn} onClick={() => downloadPdf(doc._id, doc.fileName)}>PDF</button>
                      <button type="button" className={styles.emailBtn} disabled={sending || !doc.client?.email} onClick={() => sendOne(doc._id)}>
                        {doc.emailStatus === 'sent' ? 'Renvoyer' : 'Envoyer'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmModal
        open={!!confirmModal}
        variant={confirmModal?.variant || 'info'}
        title={confirmModal?.title}
        message={confirmModal?.message}
        detail={confirmModal?.detail}
        confirmLabel={confirmModal?.confirmLabel || 'Envoyer'}
        onConfirm={handleConfirmModal}
        onCancel={() => setConfirmModal(null)}
        loading={sending}
      />
    </div>
  )
}

export default CorporateCampagneDetail
